import React from 'react';
import Icon from '../../../components/AppIcon';

const TransactionTrendingChart = ({ trendData, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!trendData || trendData?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Transaction Trends</h3>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="TrendingUp" size={48} className="mb-2" />
          <p>No trend data available</p>
        </div>
      </div>
    );
  }

  const maxAmount = Math.max(...trendData?.map(d => d?.total_amount || 0));
  const maxCount = Math.max(...trendData?.map(d => d?.transaction_count || 0));

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Transaction Trends</h3>
          <p className="text-sm text-muted-foreground">Real-time transaction volume and revenue</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-muted-foreground">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success"></div>
            <span className="text-muted-foreground">Volume</span>
          </div>
        </div>
      </div>

      <div className="relative h-64">
        <div className="absolute inset-0 flex items-end justify-between gap-2">
          {trendData?.map((item, index) => {
            const amountHeight = (item?.total_amount / maxAmount) * 100;
            const countHeight = (item?.transaction_count / maxCount) * 100;

            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center gap-1 h-48">
                  <div
                    className="w-1/2 bg-primary rounded-t transition-all hover:opacity-80 cursor-pointer group relative"
                    style={{ height: `${amountHeight}%` }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      ${item?.total_amount?.toFixed(2)}
                    </div>
                  </div>
                  <div
                    className="w-1/2 bg-success rounded-t transition-all hover:opacity-80 cursor-pointer group relative"
                    style={{ height: `${countHeight}%` }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {item?.transaction_count} txns
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground text-center">
                  {item?.date}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div>
          <p className="text-sm text-muted-foreground">Total Revenue</p>
          <p className="text-xl font-bold text-foreground">
            ${trendData?.reduce((sum, d) => sum + (d?.total_amount || 0), 0)?.toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Total Transactions</p>
          <p className="text-xl font-bold text-foreground">
            {trendData?.reduce((sum, d) => sum + (d?.transaction_count || 0), 0)}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Avg Transaction</p>
          <p className="text-xl font-bold text-foreground">
            $
            {(
              trendData?.reduce((sum, d) => sum + (d?.total_amount || 0), 0) /
              trendData?.reduce((sum, d) => sum + (d?.transaction_count || 0), 0)
            )?.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransactionTrendingChart;